import React from 'react';
import type { SkeletonProps, SkeletonVariant } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toCSSValue(value: string | number): string {
  return typeof value === 'number' ? `${value}px` : value;
}

// ─── Variant renderers ────────────────────────────────────────────────────────

const BASE = 'bg-neutral-200';

function shimmerClass(animated: boolean) {
  return animated ? 'animate-pulse-soft' : '';
}

function block(
  extra: string,
  width: string,
  height: string,
  animated: boolean,
  className?: string,
) {
  return (
    <span
      aria-hidden="true"
      className={[BASE, shimmerClass(animated), extra, className ?? ''].filter(Boolean).join(' ')}
      style={{ width, height, display: 'block' }}
    />
  );
}

// text — rounded rectangle at text height
function SkeletonText({
  width,
  height,
  animated,
  className,
}: {
  width: string;
  height: string;
  animated: boolean;
  className?: string;
}) {
  return block('rounded', width, height, animated, className);
}

// circle — avatar/icon placeholder
function SkeletonCircle({
  width,
  height,
  animated,
  className,
}: {
  width: string;
  height: string;
  animated: boolean;
  className?: string;
}) {
  return block('rounded-full', width, height, animated, className);
}

// rect — arbitrary rectangle, no rounding
function SkeletonRect({
  width,
  height,
  animated,
  className,
}: {
  width: string;
  height: string;
  animated: boolean;
  className?: string;
}) {
  return block('rounded-md', width, height, animated, className);
}

// table-row — row of cell-width pills
function SkeletonTableRow({ animated, className }: { animated: boolean; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={['flex items-center gap-4 w-full', className ?? ''].filter(Boolean).join(' ')}
      style={{ display: 'flex' }}
    >
      {[40, 120, 80, 80, 60].map((w, i) => (
        <span
          key={i}
          className={[BASE, shimmerClass(animated), 'rounded h-4 shrink-0'].filter(Boolean).join(' ')}
          style={{ width: w, display: 'block' }}
        />
      ))}
      <span
        className={[BASE, shimmerClass(animated), 'rounded h-4 flex-1'].filter(Boolean).join(' ')}
        style={{ display: 'block' }}
      />
    </span>
  );
}

// card — card outline with avatar + text lines
function SkeletonCard({ animated, className }: { animated: boolean; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'block rounded-lg border border-neutral-100 bg-white p-4 shadow-sm w-full',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ display: 'block' }}
    >
      {/* Header row: avatar + title */}
      <span className="flex items-center gap-3 mb-4" style={{ display: 'flex' }}>
        <span
          className={[BASE, shimmerClass(animated), 'rounded-full shrink-0'].filter(Boolean).join(' ')}
          style={{ width: 40, height: 40, display: 'block' }}
        />
        <span className="flex-1 space-y-2" style={{ display: 'block' }}>
          <span
            className={[BASE, shimmerClass(animated), 'rounded h-4 block w-3/4'].filter(Boolean).join(' ')}
            style={{ display: 'block' }}
          />
          <span
            className={[BASE, shimmerClass(animated), 'rounded h-3 block w-1/2'].filter(Boolean).join(' ')}
            style={{ display: 'block' }}
          />
        </span>
      </span>
      {/* Body lines */}
      {[1, 0.9, 0.6].map((fraction, i) => (
        <span
          key={i}
          className={[BASE, shimmerClass(animated), 'rounded h-3 block mb-2'].filter(Boolean).join(' ')}
          style={{ width: `${fraction * 100}%`, display: 'block' }}
        />
      ))}
    </span>
  );
}

// ─── Variant dispatch ─────────────────────────────────────────────────────────

const VARIANT_MAP: Record<
  SkeletonVariant,
  (props: {
    width: string;
    height: string;
    animated: boolean;
    className?: string;
  }) => React.ReactElement
> = {
  text: (p) => <SkeletonText {...p} />,
  circle: (p) => <SkeletonCircle {...p} />,
  rect: (p) => <SkeletonRect {...p} />,
  'table-row': (p) => <SkeletonTableRow animated={p.animated} className={p.className} />,
  card: (p) => <SkeletonCard animated={p.animated} className={p.className} />,
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

export function Skeleton({
  variant = 'text',
  width = '100%',
  height = '1em',
  count = 1,
  animated = true,
  className,
}: SkeletonProps) {
  const w = toCSSValue(width);
  const h = toCSSValue(height);
  const render = VARIANT_MAP[variant];
  const items = Array.from({ length: count });

  return (
    <span
      aria-busy="true"
      aria-label="Loading"
      className="block"
      style={{ display: 'block' }}
    >
      {items.map((_, i) => (
        <span
          key={i}
          className={count > 1 ? 'block mb-2 last:mb-0' : 'block'}
          style={{ display: 'block' }}
        >
          {render({ width: w, height: h, animated, className })}
        </span>
      ))}
    </span>
  );
}
